/**
 * apiClient.js — Centralized API client with Entra ID token acquisition.
 *
 * Wraps the native fetch API to automatically acquire an access token from
 * MSAL and attach it as an Authorization: Bearer header on every request.
 *
 * When Entra ID is not configured (no clientId), requests are sent without
 * auth headers (local dev mode).
 *
 * Usage:
 *   import { apiFetch, getAuthHeaders } from './apiClient';
 *
 *   // Drop-in replacement for fetch:
 *   const res = await apiFetch('/api/orgs');
 *
 *   // For EventSource (SSE), get headers to pass manually:
 *   const headers = await getAuthHeaders();
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig } from './authConfig';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Use openid scopes to acquire an ID token — the backend validates the ID token
// directly rather than requiring a separate access token with an api:// scope.
// This avoids the AADSTS90009 error when the app doesn't expose an API scope.
const TOKEN_SCOPES = ['openid', 'profile', 'email'];

// Singleton MSAL instance — reuse the same one created in index.js
let _msalInstance = null;

/**
 * Get or create the MSAL PublicClientApplication instance.
 * This reuses the same instance across the app.
 */
function getMsalInstance() {
  if (_msalInstance) return _msalInstance;
  if (!msalConfig.auth.clientId) return null;
  _msalInstance = new PublicClientApplication(msalConfig);
  return _msalInstance;
}

/**
 * Set the MSAL instance from outside (called by index.js to share the instance).
 */
export function setMsalInstance(instance) {
  _msalInstance = instance;
}

/**
 * Silently acquire an ID token for the backend API.
 * Falls back to interactive login if silent acquisition fails.
 *
 * Returns the ID token string, or null if auth is not configured.
 */
async function acquireToken() {
  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    return null; // Auth not configured — local dev mode
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    return null; // No signed-in user
  }

  const tokenRequest = {
    scopes: TOKEN_SCOPES,
    account: accounts[0],
  };

  try {
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    return response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired or consent needed — trigger interactive flow
      try {
        const response = await msalInstance.acquireTokenPopup(tokenRequest);
        return response.idToken;
      } catch (popupError) {
        console.error('Failed to acquire token interactively:', popupError);
        return null;
      }
    }
    console.error('Failed to acquire token silently:', error);
    return null;
  }
}

/**
 * Get Authorization headers for use with EventSource or other non-fetch APIs.
 * Returns an object like { Authorization: 'Bearer <token>' } or empty object.
 */
export async function getAuthHeaders() {
  const token = await acquireToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Authenticated fetch wrapper. Drop-in replacement for fetch() that
 * automatically attaches the Entra ID access token.
 *
 * @param {string} url - The URL to fetch (relative paths are prefixed with API_BASE)
 * @param {RequestInit} options - Standard fetch options
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  // Prefix relative URLs with API_BASE
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  // Acquire token and merge Authorization header
  const token = await acquireToken();
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(fullUrl, { ...options, headers });
}

/**
 * Build a full SSE URL with auth token as a query parameter.
 * EventSource doesn't support custom headers, so we pass the token
 * as a query param and the backend accepts it from there as well.
 *
 * @param {string} path - The SSE endpoint path (e.g., '/api/stream?thread_id=xxx')
 * @returns {Promise<string>} The full URL with token appended
 */
export async function buildAuthenticatedSSEUrl(path) {
  const baseUrl = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const token = await acquireToken();
  if (!token) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}access_token=${encodeURIComponent(token)}`;
}

export { API_BASE };
